import { Component, Input, ViewChild } from "@angular/core";
import { Router } from '@angular/router';
import { NgbDropdown } from "@ng-bootstrap/ng-bootstrap";
import moment from 'moment';
import { Subscription } from 'rxjs';
import { getPagination, PaginationParams } from 'src/app/common/pagination/pagination.service';
import { AccessManagerService } from '../../auth/accessManager/accessManager.service';
import { BrowserUtilsService } from "../../util/browserUtils.service";
import { ModalDialogComponent } from "../modal/modalDialog.component";
import { OfficeSelectorService } from "../officeSelector/officeSelector.service";
import { NoveltyService } from '../../novelties/services/novelty.service';
import { HelpCfgService } from './helpCfg.service';
import { NoveltyModel } from '../../novelties/models/novelty.model';
import { ApigeeService } from "src/app/auth/apigee/apigee.service";

@Component({
    selector: 'help',
    templateUrl: './help.component.html',
    styleUrls: ['./help.component.css'],
    standalone: false
})
export class HelpComponent {
    isVisibleNoveltyManteinanceOption: boolean = false;
    isVisibleNoveltiesHistoricOption: boolean = false;

    private selectedUserSubscription: Subscription;
    private companies: string[];

    noveltiesPagination: NoveltyModel[][] = [];
    paginationParams = new PaginationParams(10, 1, 0);

    @ViewChild(NgbDropdown) private dropdown: NgbDropdown;
    @Input() public compactView: boolean = true;

    @ViewChild(ModalDialogComponent, { static: true }) private dialog: ModalDialogComponent;

    public dialogLoading: boolean = false;
    public novelties: NoveltyModel[];

    public get showTourOption(): boolean {
        return !this.browserUtilsService.isMobileBrowser() && this.router.url == "/dashboard";
    }

    public get showNoveltyManteinanceOption(): boolean {
        return this.isVisibleNoveltyManteinanceOption;
    }

    public get showNoveltiesHistoric(): boolean {
        return this.isVisibleNoveltiesHistoricOption;
    }

    // Ordenamiento
    currentFilterParams = {
        properties: 'iDate',
        typeOrder: 'ASC'
    };

    constructor(private helpCfgService: HelpCfgService,
        private router: Router,
        private browserUtilsService: BrowserUtilsService,
        private accessManagerService: AccessManagerService,
        private officeSelectorService: OfficeSelectorService,
        private noveltyService: NoveltyService,
        private apigeeService: ApigeeService) {
    }

    async ngOnInit(): Promise<void> {
        this.selectedUserSubscription = this.apigeeService.authTokensUpdated.subscribe(() => {
            this.initializeData();
        });

        this.accessManagerService.getResourcePermissions(4096).then(x => {
            const action = x.mResourceActions.find(r => r.ActionTypeId == 2);
            this.isVisibleNoveltyManteinanceOption = action && action.AllowChk;
        });
    }

    ngOnDestroy(): void {
        if (this.selectedUserSubscription) {
            this.selectedUserSubscription.unsubscribe();
        }
    }

    /**
     * Inicializa la data para mostrar en la modal
     */
    initializeData(): void {
        if (this.apigeeService.hasToken) {
            this.officeSelectorService.getOfficeList(this.apigeeService.userInfo.nameid).then(companiesOffice => {
                this.companies = companiesOffice.map(x => x.id);
                this.getNovelties();
            });
        }
    }

    public onHelp(event: Event): void {
        if (event) event.stopPropagation();
        this.dropdown.close();
        window.open(this.helpCfgService.getCfg().link, '_blank');
    }

    public onTour(event: Event): void {
        if (event) event.stopPropagation();
        this.dropdown.close();
        let dashboardRoute = this.router.config.find(x => x.path == "dashboard");
        dashboardRoute.data = { forceTour: true };
        this.router.navigate(['dashboard']);
    }

    public getNovelties(): void {
        this.novelties = [];
        this.noveltiesPagination[0] = [];
        this.dialogLoading = true;
        this.noveltyService.getHistorical(this.companies).subscribe(data => {
            this.novelties = data.novelties;
            this.novelties.forEach(x => {
                x.begDate = moment(x.begDate).format("DD/MM/YYYY");
                x.endDate = moment(x.endDate).format("DD/MM/YYYY");
            });

            this.novelties = this.novelties.sort((a, b) => moment(b.iDate).valueOf() - moment(a.iDate).valueOf());

            this.noveltiesPagination = getPagination(this.paginationParams, this.novelties);

            this.dialogLoading = false;
            this.isVisibleNoveltiesHistoricOption = this.novelties.length > 0;
        }, () => {
            this.dialogLoading = false;
        });
    }

    public async downloadAttachedName(noveltyId: number) {
        this.dialogLoading = true;

        this.noveltyService.getUrlAttachment(noveltyId).subscribe(data => {
            this.dialogLoading = false;
            window.open(data.urlAttachment, '_blank');
        }, () => {
            this.dialogLoading = false;
        });
    }

    public async openDialogNoveltiesHistoric(event: Event) {
        this.dropdown.close();
        this.dialog.open();
        this.getNovelties();
    }

    public closeDialogNoveltiesHistoric(): void {
        this.dialog.open();
    }

    public onNoveltyMaintenance(): void {
        this.router.navigate(['novelty/maintenance']);
    }

    /**
   * Gestiona el componente de paginación
   * @param page Página a mostrar
   */
    pagination(page: number): void {
        if (page !== this.paginationParams.currentPage) {
            this.paginationParams.currentPage = page;
            this.paginationParams.currentIndex = page - 1;
        }
    }

    /**
   * Permite ordenar la grilla
   * @param column columna a ordenar
   */
    orderBy(column: string) {
        this.pagination(0);

        if (this.currentFilterParams.properties != column) {
            this.currentFilterParams = {
                properties: '',
                typeOrder: ''
            };
        }

        this.currentFilterParams.properties = column;

        if (this.currentFilterParams.typeOrder == '') {
            this.currentFilterParams.typeOrder = 'ASC';
        } else if (this.currentFilterParams.typeOrder == 'ASC') {
            this.currentFilterParams.typeOrder = 'DESC';
        } else {
            this.currentFilterParams.properties = 'iDate';
            this.currentFilterParams.typeOrder = 'DESC';
        }

        this.sortBy();
    }

    /**
     * Ordena de acuerdo a los parémetros seteados
     */
    sortBy() {
        if (['begDate', 'endDate'].includes(this.currentFilterParams.properties)) {
            if (this.currentFilterParams.typeOrder === 'ASC') {
                this.novelties = this.novelties.sort((a, b) => moment(a[this.currentFilterParams.properties], 'DD/MM/YYYY').valueOf() - moment(b[this.currentFilterParams.properties], 'DD/MM/YYYY').valueOf());
            }
            else {
                this.novelties = this.novelties.sort((a, b) => moment(b[this.currentFilterParams.properties], 'DD/MM/YYYY').valueOf() - moment(a[this.currentFilterParams.properties], 'DD/MM/YYYY').valueOf());
            }
        }
        else {
            if (this.currentFilterParams.typeOrder === 'ASC') {
                this.novelties = this.novelties.sort((a, b) => a[this.currentFilterParams.properties].localeCompare(b[this.currentFilterParams.properties]));
            }
            else {
                this.novelties = this.novelties.sort((a, b) => b[this.currentFilterParams.properties].localeCompare(a[this.currentFilterParams.properties]));
            }
        }

        let count = 0;
        for (let i = 0; i < this.novelties.length; i += this.paginationParams.pageMaxSize) {
            let nextIndex = Math.min(this.paginationParams.pageMaxSize * (count + 1), this.novelties.length);
            let currentList = this.novelties.slice(i, nextIndex);
            this.noveltiesPagination[count] = currentList;
            count++;
        }
    }
}
